import { MultiEntryMappingsUtil } from "@eagerdog/constants";
import { IDogClass } from "@eagerdog/interfaces";

// Extended interface for FormBuilder usage
interface IDogClassElement extends IDogClass {
  id: string;
  start_minutes?: number;
  time_slot?: string;
  score_id?: string;
  // CAT-specific fields
  plan_to_earn_title?: string;
  title?: string;
  is_brachycephalic?: boolean;
}

export class MultiEntryMappingService {

  /**
   * Check if a class level is a multi-entry type
   */
  static isMultiEntry(level: string, sanctioningClub: string, showType: string): boolean {
    return MultiEntryMappingsUtil.isMultiEntry(level, sanctioningClub, showType);
  }

  /**
   * Get the constituent classes for a multi-entry
   */
  static getConstituentClasses(level: string, sanctioningClub: string, showType: string): string[] {
    return MultiEntryMappingsUtil.getConstituentClasses(level, sanctioningClub, showType);
  }

  /**
   * Get multi-entries that contain a specific individual class
   */
  static getRelatedMultiEntries(level: string, sanctioningClub: string, showType: string, section?: string | null): string[] {
    const relatedMultis = MultiEntryMappingsUtil.getRelatedMultiEntries(level, sanctioningClub, showType, section);
    return relatedMultis.map(mapping => mapping.multiEntryLevel);
  }

  /** Constituent lists use "RO2 B" labels; UKC Rally classes may be level + section. */
  private static classLabel(dogClass: { level: string; section?: string | null }, sanctioningClub: string, showType: string): string {
    return MultiEntryMappingsUtil.classLabel(dogClass.level, dogClass.section, sanctioningClub, showType);
  }

  /**
   * Returns true if this multi-entry level disables ALL other options in the
   * same show element when selected (e.g. URX, RMX for UKC Rally).
   */
  static isDisablesAll(level: string, sanctioningClub: string, showType: string): boolean {
    return MultiEntryMappingsUtil.isDisablesAll(level, sanctioningClub, showType);
  }

  /**
   * Get classes that should be disabled based on current selection
   */
  static getDisabledClasses(selectedClasses: IDogClassElement[], sanctioningClub: string, showType: string): string[] {
    const disabledLevels: string[] = [];

    selectedClasses.forEach(selectedClass => {
      if (this.isMultiEntry(selectedClass.level, sanctioningClub, showType)) {
        // If a multi-entry is selected, disable its constituent classes
        const constituents = this.getConstituentClasses(selectedClass.level, sanctioningClub, showType);
        disabledLevels.push(...constituents);
      } else {
        // If an individual class is selected, disable related multi-entries
        const relatedMultis = this.getRelatedMultiEntries(selectedClass.level, sanctioningClub, showType, selectedClass.section);
        disabledLevels.push(...relatedMultis);
      }
    });

    return [...new Set(disabledLevels)]; // Remove duplicates
  }

  /**
   * Check for conflicts when trying to add a new class
   */
  static getConflictingClasses(selectedClasses: IDogClassElement[], newClass: IDogClassElement, sanctioningClub: string, showType: string): string[] {
    const conflicts: string[] = [];

    // Check if the new class is a multi-entry
    if (this.isMultiEntry(newClass.level, sanctioningClub, showType)) {
      const constituents = this.getConstituentClasses(newClass.level, sanctioningClub, showType);
      
      // Check if any constituent classes are already selected
      selectedClasses.forEach(selectedClass => {
        if (constituents.includes(this.classLabel(selectedClass, sanctioningClub, showType))) {
          const displayName = MultiEntryMappingsUtil.getDisplayName(selectedClass.level, sanctioningClub, showType);
          conflicts.push(displayName || this.classLabel(selectedClass, sanctioningClub, showType));
        }
      });
    } else {
      // Check if the new individual class conflicts with any selected multi-entries
      selectedClasses.forEach(selectedClass => {
        if (this.isMultiEntry(selectedClass.level, sanctioningClub, showType)) {
          const constituents = this.getConstituentClasses(selectedClass.level, sanctioningClub, showType);
          if (constituents.includes(this.classLabel(newClass, sanctioningClub, showType))) {
            const displayName = MultiEntryMappingsUtil.getDisplayName(selectedClass.level, sanctioningClub, showType);
            conflicts.push(displayName || selectedClass.level);
          }
        }
      });
    }

    return conflicts;
  }



  /**
   * Check for conflicts with existing registrations (from previous registrations)
   */
  static getExistingRegistrationConflicts(existingClasses: (IDogClass | IDogClassElement)[], newClass: IDogClassElement, sanctioningClub: string, showType: string): string[] {
    const conflicts: string[] = [];

    // Check if the new class is a multi-entry
    if (this.isMultiEntry(newClass.level, sanctioningClub, showType)) {
      const constituents = this.getConstituentClasses(newClass.level, sanctioningClub, showType);
      
      // Check if any constituent classes are already registered
      existingClasses.forEach(existingClass => {
        const label = this.classLabel(existingClass, sanctioningClub, showType);
        if (constituents.includes(label)) {
          // Use the actual class name for the conflict message
          conflicts.push(label);
        }
      });
    }

    return conflicts;
  }

  /**
   * Validate if a class selection is allowed (checks both current form and existing registrations)
   */
  static validateClassSelection(selectedClasses: IDogClassElement[], newClass: IDogClassElement, sanctioningClub: string, showType: string, existingClasses?: (IDogClass | IDogClassElement)[]): {
    isValid: boolean;
    errorMessage?: string;
  } {
    // Check conflicts within current form selection
    const formConflicts = this.getConflictingClasses(selectedClasses, newClass, sanctioningClub, showType);
    
    if (formConflicts.length > 0) {
      return {
        isValid: false,
        errorMessage: `Dog is already entered in ${formConflicts.join(', ')}`
      };
    }

    // Check conflicts with existing registrations
    if (existingClasses && existingClasses.length > 0) {
      const existingConflicts = this.getExistingRegistrationConflicts(existingClasses, newClass, sanctioningClub, showType);
      
      if (existingConflicts.length > 0) {
        return {
          isValid: false,
          errorMessage: `Dog is already registered for ${existingConflicts.join(', ')} in a previous registration`
        };
      }
    }
    
    return { isValid: true };
  }
}
